import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';

/**
 * Handles the OAuth callback redirect from Google.
 * 
 * Route: /settings/google/callback?code=xxx&state=xxx
 * 
 * Exchanges the authorization code for tokens via the backend,
 * then redirects to the Connections page.
 */
function GoogleCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('Connecting your Google account...');
  const [error, setError] = useState(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError(`Google declined the connection: ${errorParam}`);
      return;
    }

    if (!code || !state) {
      setError('Missing authorization code. Please try connecting again.');
      return;
    }

    const exchangeCode = async () => {
      try {
        setStatus('Exchanging authorization code...');

        const data = await api.request('/platforms/google/callback', {
          method: 'POST',
          body: JSON.stringify({ code, state }),
        });

        setStatus(`Connected: ${data.location_name}`);

        // Redirect to connections page after a brief pause
        setTimeout(() => {
          navigate('/connections', {
            state: { success: `Google Business Profile connected: ${data.location_name}` },
          });
        }, 1500);
      } catch (err) {
        setError(err.message);
      }
    };

    exchangeCode();
  }, [searchParams, navigate]);

  return (
    <div className="page-container">
      <div className="oauth-callback">
        {error ? (
          <div className="oauth-error">
            <div className="oauth-icon error-icon">✕</div>
            <h2>Connection Failed</h2>
            <p>{error}</p>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/connections')}
            >
              Back to Connections
            </button>
          </div>
        ) : (
          <div className="oauth-loading">
            <div className="oauth-spinner"></div>
            <p>{status}</p>
          </div>
        )}
      </div>

      <style>{`
        .oauth-callback {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 300px;
        }
        .oauth-loading, .oauth-error {
          text-align: center;
        }
        .oauth-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid var(--border, #e5e7eb);
          border-top-color: var(--accent, #7c3aed);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto 16px;
        }
        .oauth-icon {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          margin: 0 auto 16px;
        }
        .error-icon {
          background: #fef2f2;
          color: #dc2626;
        }
        .oauth-error h2 {
          margin-bottom: 8px;
        }
        .oauth-error p {
          color: var(--text-secondary, #6b7280);
          margin-bottom: 20px;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default GoogleCallback;
